import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Scale, Plus, RefreshCw, Trash2, Pencil, FileText, ExternalLink,
  ShieldCheck, ShieldAlert, ShieldX, MinusCircle, Clock, AlertOctagon, Filter,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const STATUS_ICON = {
  "Compliant": ShieldCheck, "Partially Compliant": ShieldAlert, "Non-Compliant": ShieldX,
  "Not Applicable": MinusCircle, "Under Review": Clock,
};
const STATUS_TONE = {
  "Compliant": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Partially Compliant": "bg-amber-100 text-amber-800 border-amber-200",
  "Non-Compliant": "bg-rose-100 text-rose-800 border-rose-200",
  "Not Applicable": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Review": "bg-blue-100 text-blue-800 border-blue-200",
};
const CATEGORY_TONE = {
  "ISO Standard": "bg-indigo-100 text-indigo-700",
  "Environmental": "bg-emerald-100 text-emerald-700",
  "Health & Safety": "bg-rose-100 text-rose-700",
  "Data Privacy": "bg-purple-100 text-purple-700",
  "Employment": "bg-sky-100 text-sky-700",
  "Financial": "bg-amber-100 text-amber-700",
  "Industry-Specific": "bg-cyan-100 text-cyan-700",
  "Other": "bg-slate-100 text-slate-700",
};

export default function LegalRequirementsPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const qp = params.get("client");
  const [clientSlug, setClientSlug] = useState(qp || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState({ categories: [], statuses: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, edit: null });
  const isClient = ["client", "client_admin", "client_employee"].includes(scope?.role);
  const readOnly = scope?.role === "auditor";

  const load = () => {
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.legalMetadata().then(setMeta).catch(() => {});
    endpoints.listLegalRequirements({ client_slug: clientSlug }).then(setItems).catch(() => setItems([]));
    endpoints.legalDashboard(clientSlug).then(setDash).catch(() => setDash(null));
    endpoints.listDocuments({ client_slug: clientSlug }).then(setDocuments).catch(() => setDocuments([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const filtered = useMemo(() => items.filter(x =>
    (statusFilter === "all" || x.compliance_status === statusFilter) &&
    (categoryFilter === "all" || x.category === categoryFilter)
  ), [items, statusFilter, categoryFilter]);

  const del = async (id) => {
    if (!window.confirm("Remove this legal requirement from the register?")) return;
    try { await endpoints.deleteLegalRequirement(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 text-white flex items-center justify-center shadow-md">
            <Scale className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Legal & Regulatory Register</h1>
            <p className="text-xs text-slate-500">Track applicable laws, regulations and compliance status.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="legal-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="legal-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          {!readOnly && (
            <Button className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, edit: null })} data-testid="legal-add">
              <Plus className="h-4 w-4 mr-1" /> New Requirement
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <StatCard label="Total" value={dash.total} tone="indigo" testid="legal-stat-total" />
          <StatCard label="Compliant" value={`${dash.compliant_pct}%`} tone="emerald" testid="legal-stat-compliant" />
          <StatCard label="Overdue" value={dash.overdue} tone="rose" testid="legal-stat-overdue" icon={<AlertOctagon className="h-3 w-3" />} />
          <StatCard label="Due ≤30d" value={dash.due_30} tone="amber" testid="legal-stat-30" />
          <StatCard label="Due ≤60d" value={dash.due_60} tone="orange" testid="legal-stat-60" />
          <StatCard label="Due ≤90d" value={dash.due_90} tone="cyan" testid="legal-stat-90" />
        </div>
      )}

      {/* Status pills */}
      {dash && (
        <Card className="glass-card mb-4"><CardContent className="p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mr-2">Compliance</span>
            {Object.entries(dash.by_status).map(([s, c]) => {
              const Icon = STATUS_ICON[s] || Clock;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${statusFilter === s ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`legal-pill-${s}`}>
                  <Icon className="h-3 w-3" />
                  {s} <span className="font-bold">{c}</span>
                </button>
              );
            })}
            {statusFilter !== "all" && <Button variant="ghost" size="sm" onClick={() => setStatusFilter("all")}>Clear</Button>}
          </div>
        </CardContent></Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="legal-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">{filtered.length} of {items.length}</span>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-2xl bg-slate-50/50">
          <Scale className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500 mb-3">No legal requirements match the current filters.</div>
          {!readOnly && <Button onClick={() => setModal({ open: true, edit: null })} className="brand-gradient text-white"><Plus className="h-4 w-4 mr-1" /> Add first requirement</Button>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(x => (
            <LegalCard key={x.id} item={x} documents={documents} readOnly={readOnly}
              onEdit={() => setModal({ open: true, edit: x })}
              onDelete={() => del(x.id)} />
          ))}
        </div>
      )}

      <LegalModal open={modal.open} edit={modal.edit} clientSlug={clientSlug} meta={meta} documents={documents}
        onClose={() => setModal({ open: false, edit: null })}
        onSaved={() => { setModal({ open: false, edit: null }); load(); }} />
    </div>
  );
}

function StatCard({ label, value, tone, testid, icon }) {
  const g = {
    indigo: "from-indigo-500 to-purple-600", rose: "from-rose-500 to-orange-500",
    amber: "from-amber-500 to-orange-500", cyan: "from-cyan-500 to-teal-500",
    emerald: "from-emerald-500 to-teal-500", orange: "from-orange-500 to-red-500",
  }[tone] || "from-indigo-500 to-purple-600";
  return (
    <Card className="glass-card overflow-hidden" data-testid={testid}>
      <div className={`h-1 bg-gradient-to-r ${g}`} />
      <CardContent className="p-3">
        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{icon}{label}</div>
        <div className="text-2xl font-extrabold font-display mt-0.5">{value}</div>
      </CardContent>
    </Card>
  );
}

function LegalCard({ item, documents, readOnly, onEdit, onDelete }) {
  const StatusIcon = STATUS_ICON[item.compliance_status] || Clock;
  const linkedDocs = (item.linked_document_ids || [])
    .map(id => documents.find(d => d.id === id))
    .filter(Boolean);
  const dueBadge = item.overdue
    ? <Badge className="bg-rose-600 text-white text-[10px] rounded-full"><AlertOctagon className="h-2.5 w-2.5 mr-1" />Review overdue</Badge>
    : item.due_soon
      ? <Badge className="bg-amber-500 text-white text-[10px] rounded-full">Review due soon</Badge>
      : null;
  return (
    <Card className="glass-card" data-testid={`legal-item-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusIcon className="h-4 w-4 text-slate-400" />
              <div className="font-semibold truncate">{item.title}</div>
              {item.reference_number && <span className="font-mono text-xs text-slate-500">{item.reference_number}</span>}
              <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[item.compliance_status]}`}>{item.compliance_status}</Badge>
              <Badge className={`text-[10px] rounded-full ${CATEGORY_TONE[item.category] || CATEGORY_TONE.Other}`}>{item.category}</Badge>
              {dueBadge}
            </div>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {item.jurisdiction && <span>{item.jurisdiction}</span>}
              {item.applicable_clauses && <span>Clauses: {item.applicable_clauses}</span>}
              {item.responsible_person && <span>Owner: {item.responsible_person}</span>}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Last reviewed {fmt(item.review_date)} · Next review {fmt(item.next_review_due)}
              {item.due_in_days !== null && item.due_in_days !== undefined && (
                <span className={`ml-2 font-mono ${item.overdue ? "text-rose-600" : item.due_soon ? "text-amber-600" : "text-slate-500"}`}>
                  ({item.due_in_days < 0 ? `${Math.abs(item.due_in_days)}d overdue` : `in ${item.due_in_days}d`})
                </span>
              )}
            </div>
            {linkedDocs.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {linkedDocs.map(d => (
                  <a key={d.id} href={`/documents?open=${d.id}&client=${item.client_slug}`}
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-100">
                    <FileText className="h-3 w-3" /> {d.title || "Doc"} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ))}
              </div>
            )}
            {item.notes && <div className="text-xs italic text-slate-600 mt-2 border-l-2 border-indigo-300 pl-2">{item.notes}</div>}
          </div>
          <div className="flex gap-1 shrink-0">
            {!readOnly && (
              <>
                <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`legal-edit-${item.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`legal-delete-${item.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LegalModal({ open, edit, clientSlug, meta, documents, onClose, onSaved }) {
  const empty = {
    title: "", reference_number: "", category: "Other", jurisdiction: "",
    applicable_clauses: "", compliance_status: "Under Review", review_date: "",
    next_review_due: "", responsible_person: "", linked_document_ids: [], notes: "",
  };
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(edit ? { ...empty, ...edit, linked_document_ids: edit.linked_document_ids || [] } : empty); /* eslint-disable-next-line */ }, [open, edit]);

  const toggleDoc = (id) => {
    setF(p => {
      const ids = p.linked_document_ids || [];
      return { ...p, linked_document_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
    });
  };

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    try {
      if (edit?.id) {
        const { id, client_slug, created_at, updated_at, created_by, overdue, due_soon, due_in_days, ...updates } = f;
        await endpoints.updateLegalRequirement(edit.id, updates);
        toast.success("Updated");
      } else {
        await endpoints.createLegalRequirement({ ...f, client_slug: clientSlug });
        toast.success("Added to register");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="legal-modal">
        <DialogHeader><DialogTitle>{edit ? "Edit legal requirement" : "New legal requirement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="legal-title" placeholder="e.g. GDPR – EU 2016/679" />
            </div>
            <div>
              <Label className="text-xs">Reference #</Label>
              <Input value={f.reference_number} onChange={e => setF({ ...f, reference_number: e.target.value })} placeholder="Regulation number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="legal-category"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Jurisdiction</Label>
              <Input value={f.jurisdiction} onChange={e => setF({ ...f, jurisdiction: e.target.value })} placeholder="e.g. European Union, UK, US" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Applicable clauses / articles</Label>
            <Input value={f.applicable_clauses} onChange={e => setF({ ...f, applicable_clauses: e.target.value })} placeholder="e.g. Art. 5, 30, 32" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Compliance status</Label>
              <Select value={f.compliance_status} onValueChange={v => setF({ ...f, compliance_status: v })}>
                <SelectTrigger data-testid="legal-status"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Last reviewed</Label>
              <Input type="date" value={f.review_date || ""} onChange={e => setF({ ...f, review_date: e.target.value })} data-testid="legal-review-date" />
            </div>
            <div>
              <Label className="text-xs">Next review due</Label>
              <Input type="date" value={f.next_review_due || ""} onChange={e => setF({ ...f, next_review_due: e.target.value })} data-testid="legal-next-due" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Responsible person</Label>
            <Input value={f.responsible_person} onChange={e => setF({ ...f, responsible_person: e.target.value })} placeholder="e.g. Data Protection Officer" />
          </div>
          <div>
            <Label className="text-xs">Linked Controlled Documents ({(f.linked_document_ids || []).length})</Label>
            <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-white/10 p-2 bg-white/40 dark:bg-slate-900/40 space-y-1" data-testid="legal-linked-docs">
              {documents.length === 0 ? (
                <div className="text-xs text-slate-500 italic p-2">No controlled documents for this client yet.</div>
              ) : (
                documents.map(d => (
                    <label key={d.id} className="flex items-start gap-2 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/50 px-2 py-1 cursor-pointer">
                    <Checkbox checked={(f.linked_document_ids || []).includes(d.id)} onCheckedChange={() => toggleDoc(d.id)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.title}</div>
                      <div className="text-[10px] text-slate-500">{d.doc_type || d.type || "—"} · {d.doc_number || "—"} · {d.status}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Compliance notes, gaps, action items…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="brand-gradient text-white" onClick={submit} data-testid="legal-save">{edit ? "Save" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
